import {QueryKey, useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {AxiosError, AxiosResponse} from 'axios';
import axiosInstance from '../../axiosConfig';
import {
  ISharedFacilityVisibilityPayload,
  ISharedFacilityVisibilityResponse,
} from '../../dto/FacilitySharing.dto';
import {AxiosErrorToast} from '../general/ReusableFunctions';

const buildSharedFacilityVisibilityPath = (
  companyId: number,
  stationId: number,
): string => {
  return `/v1/company/${companyId}/station/${stationId}/shared-facility-visibility`;
};

export const getSharedFacilityVisibilityApiPath =
  buildSharedFacilityVisibilityPath;

export const getSharedFacilityVisibilityQueryKey = (
  companyId: number,
  stationId: number,
) => [buildSharedFacilityVisibilityPath(companyId, stationId)] as const;

const getSharedFacilityVisibilityDetail = async (
  companyId: number,
  stationId: number,
): Promise<ISharedFacilityVisibilityResponse> => {
  try {
    const response: AxiosResponse<ISharedFacilityVisibilityResponse> =
      await axiosInstance.get(
        buildSharedFacilityVisibilityPath(companyId, stationId),
      );

    return response.data;
  } catch (error) {
    const axiosError = error as AxiosError;
    AxiosErrorToast(axiosError, 'Error loading shared facility visibility');
    throw axiosError;
  }
};

const saveSharedFacilityVisibility = (
  companyId: number,
  stationId: number,
  payload: ISharedFacilityVisibilityPayload,
) => {
  return axiosInstance
    .post(buildSharedFacilityVisibilityPath(companyId, stationId), payload)
    .then((response: AxiosResponse<ISharedFacilityVisibilityResponse>) => {
      return response.data;
    })
    .catch((error: AxiosError) => {
      AxiosErrorToast(error, 'Error saving shared facility visibility');
      throw error;
    });
};

const disableSharedFacilityVisibility = (
  companyId: number,
  stationId: number,
) => {
  return axiosInstance
    .delete(buildSharedFacilityVisibilityPath(companyId, stationId))
    .then(() => {
      return;
    })
    .catch((error: AxiosError) => {
      AxiosErrorToast(error, 'Error disabling shared facility visibility');
      throw error;
    });
};

export const useRqSharedFacilityVisibilityDetail = (
  companyId: number,
  stationId: number,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: getSharedFacilityVisibilityQueryKey(companyId, stationId),
    queryFn: () => getSharedFacilityVisibilityDetail(companyId, stationId),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: enabled && Boolean(companyId && stationId),
  });
};

export const useRqSharedFacilityVisibility = (
  companyId: number,
  stationId: number,
  invalidateQueryKeys: QueryKey[] = [],
) => {
  const queryClient = useQueryClient();
  const primaryKey = getSharedFacilityVisibilityQueryKey(companyId, stationId);

  const invalidateVisibilityQueries = async () => {
    await queryClient.invalidateQueries({queryKey: primaryKey});

    await Promise.all(
      invalidateQueryKeys.map(queryKey =>
        queryClient.invalidateQueries({queryKey}),
      ),
    );
  };

  const saveMutation = useMutation({
    mutationFn: (payload: ISharedFacilityVisibilityPayload) =>
      saveSharedFacilityVisibility(companyId, stationId, payload),
    onSuccess: async () => {
      await invalidateVisibilityQueries();
    },
  });

  const disableMutation = useMutation({
    mutationFn: () => disableSharedFacilityVisibility(companyId, stationId),
    onSuccess: async () => {
      await invalidateVisibilityQueries();
    },
  });

  return {
    saveMutation,
    disableMutation,
  };
};
